import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDBA } from '@/contexts/DBAContext';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { JobsBatchBar, JobsFilters, JobsViewControls } from '@/components/jobs/JobsControls';
import { JobsCalendarViews } from '@/components/jobs/JobsCalendarViews';
import { JobDialogs } from '@/components/jobs/JobDialogs';
import { BriefcaseBusiness, Plus } from 'lucide-react';
import { HighVisibilityPageHeader } from '@/components/ui/high-visibility-mobile';
import { format, addDays, startOfWeek, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';

const statusColors = {
  tentative: 'bg-blue-100 text-blue-800 border border-blue-300',
  scheduled: 'bg-sky-100 text-sky-800 border border-sky-300',
  in_progress: 'bg-orange-100 text-orange-800 border border-orange-300',
  completed: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
  cancelled: 'bg-red-100 text-red-800 border border-red-300',
};
const allStatuses = ['tentative', 'scheduled', 'in_progress', 'completed', 'cancelled'];
const canUseProofPacket = (job) => job?.status === 'completed' && (job?.proof_packet_token || job?.closeout_photo_requirements?.met);

export default function JobsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { dbas, selectedDBA, getDBAName, getDBAColor } = useDBA();
  const [jobs, setJobs] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calView, setCalView] = useState('week'); // month | week | day
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [monthDate, setMonthDate] = useState(new Date());
  const [dayDate, setDayDate] = useState(new Date());
  const [showAdd, setShowAdd] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [staffFilter, setStaffFilter] = useState('all');
  const [error, setError] = useState('');
  const [dragJobId, setDragJobId] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [selected, setSelected] = useState(new Set());
  // New job form state for scheduling assistant
  const [newJobStaff, setNewJobStaff] = useState([]);
  const [newJobDate, setNewJobDate] = useState('');
  const [newJobTimeStart, setNewJobTimeStart] = useState('');
  const [newJobTimeEnd, setNewJobTimeEnd] = useState('');
  const [newJobAddress, setNewJobAddress] = useState('');
  const [monthJobs, setMonthJobs] = useState([]);
  // Job detail state
  const [pendingDate, setPendingDate] = useState(null);
  const [detailTime, setDetailTime] = useState('');
  const [detailConflictTimes, setDetailConflictTimes] = useState([]);
  const [detailNote, setDetailNote] = useState('');
  const [manualEta, setManualEta] = useState('15 minutes');
  const [notifyEtaCustomer, setNotifyEtaCustomer] = useState(true);
  const [lateAlerts, setLateAlerts] = useState([]);
  const [lateThreshold, setLateThreshold] = useState(15);
  const [createPrefill, setCreatePrefill] = useState(null);
  const [jobSmsMessages, setJobSmsMessages] = useState([]);
  const [jobSmsSyncing, setJobSmsSyncing] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      const params = {};
      if (selectedDBA) params.dba_id = selectedDBA;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchText) params.search = searchText;
      if (staffFilter !== 'all') params.assigned_to = staffFilter;
      if (calView === 'week') {
        params.date_from = format(weekStart, 'yyyy-MM-dd');
        params.date_to = format(addDays(weekStart, 6), 'yyyy-MM-dd');
      } else if (calView === 'day') {
        params.date = format(dayDate, 'yyyy-MM-dd');
      } else {
        params.date_from = format(startOfMonth(monthDate), 'yyyy-MM-dd');
        params.date_to = format(endOfMonth(monthDate), 'yyyy-MM-dd');
      }
      const { data } = await api.get('/jobs', { params });
      if (calView === 'month') setMonthJobs(data);
      else setJobs(data);
    } catch { return; }
    finally { setLoading(false); }
  }, [selectedDBA, statusFilter, searchText, staffFilter, calView, weekStart, dayDate, monthDate]);

  const fetchTeam = useCallback(async () => {
    try { const { data } = await api.get('/team'); setTeam(data); }
    catch { return; }
  }, []);

  const fetchLateAlerts = useCallback(async () => {
    try {
      const params = { threshold_minutes: lateThreshold };
      if (selectedDBA) params.dba_id = selectedDBA;
      const { data } = await api.get('/jobs/late-alerts', { params });
      setLateAlerts(data);
    } catch { return; }
  }, [lateThreshold, selectedDBA]);

  useEffect(() => { setLoading(true); fetchJobs(); }, [fetchJobs]);
  useEffect(() => { fetchTeam(); }, [fetchTeam]);
  useEffect(() => { fetchLateAlerts(); }, [fetchLateAlerts]);

  // Open specific job from dashboard queue click
  useEffect(() => {
    if (location.state?.openJobId) {
      const openJob = async () => {
        try {
          const { data } = await api.get(`/jobs/${location.state.openJobId}`);
          setShowDetail(data);
        } catch { return; }
      };
      openJob();
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  useEffect(() => {
    if (!location.state?.createForCustomer) return;
    const customer = location.state.createForCustomer;
    setCreatePrefill(customer);
    setNewJobStaff([]);
    setNewJobDate(format(new Date(), 'yyyy-MM-dd'));
    setNewJobTimeStart('');
    setNewJobTimeEnd('');
    setNewJobAddress(customer.address || '');
    setError('');
    setShowAdd(true);
    window.history.replaceState({}, '');
  }, [location.state]);

  const fieldStaff = team.filter(t => t.role === 'field_staff' || t.role === 'dispatcher');
  const getStaffName = (id) => team.find(t => t.id === id)?.name || id;

  const handleCreate = async (e) => {
    e.preventDefault(); setError('');
    const f = new FormData(e.target);
    try {
      await api.post('/jobs', {
        title: f.get('title'), description: f.get('description'),
        dba_id: f.get('dba_id'), customer_id: f.get('customer_id') || createPrefill?.id || '',
        scheduled_date: newJobDate || f.get('scheduled_date'),
        scheduled_time_start: newJobTimeStart,
        scheduled_time_end: newJobTimeEnd || (newJobTimeStart ? format(addHour(newJobTimeStart), 'HH:mm') : ''),
        address: newJobAddress || f.get('address'),
        service_category: f.get('service_category'),
        estimated_value: parseFloat(f.get('estimated_value') || '0'),
        assigned_staff: newJobStaff, notes: f.get('notes'),
      });
      setShowAdd(false); setCreatePrefill(null); resetNewJobForm(); fetchJobs();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  function addHour(time) {
    const [h, m] = time.split(':').map(Number);
    const nh = Math.min(h + 1, 23);
    return new Date(2000, 0, 1, nh, m);
  }

  const resetNewJobForm = () => {
    setNewJobStaff([]); setNewJobDate(''); setNewJobTimeStart(''); setNewJobTimeEnd(''); setNewJobAddress(''); setCreatePrefill(null);
  };

  const updateStatus = async (id, status) => {
    setError('');
    try {
      await api.put(`/jobs/${id}/status`, { status });
      fetchJobs();
      if (showDetail?.id === id) { const { data } = await api.get(`/jobs/${id}`); setShowDetail(data); }
      toast.success(`Job marked ${status.replace('_', ' ')}`);
    } catch (err) {
      const message = formatApiError(err.response?.data?.detail);
      setError(message);
      toast.error(message);
    }
  };

  const rescheduleJob = async (jobId, newDate) => {
    await api.put(`/jobs/${jobId}`, { scheduled_date: newDate });
    const { data } = await api.get(`/jobs/${jobId}`);
    setShowDetail(data); fetchJobs();
  };

  // DnD
  const handleDragStart = (e, jobId) => { setDragJobId(jobId); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', jobId); e.currentTarget.style.opacity = '0.4'; };
  const handleDragEnd = (e) => { e.currentTarget.style.opacity = '1'; setDragJobId(null); setDragOverDay(null); };
  const handleDragOver = (e, dayStr) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverDay(dayStr); };
  const handleDragLeave = () => setDragOverDay(null);
  const handleDrop = async (e, dayStr) => {
    e.preventDefault(); setDragOverDay(null);
    const jobId = e.dataTransfer.getData('text/plain');
    const job = jobs.find(j => j.id === jobId);
    if (!job || job.scheduled_date === dayStr) return;
    await api.put(`/jobs/${jobId}`, { scheduled_date: dayStr }); fetchJobs();
  };

  // Batch
  const toggleSelect = (id) => { setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleSelectAll = () => { const all = calView === 'month' ? monthJobs : jobs; selected.size === all.length ? setSelected(new Set()) : setSelected(new Set(all.map(j => j.id))); };
  const clearSelection = () => setSelected(new Set());
  const batchStatus = async (status) => { await api.post('/jobs/batch/status', { job_ids: [...selected], status }); clearSelection(); fetchJobs(); };
  const batchAssign = async (staffIds) => { await api.post('/jobs/batch/assign', { job_ids: [...selected], assigned_staff: staffIds }); clearSelection(); fetchJobs(); };

  // Navigation
  const goToDay = (date) => { setDayDate(date); setCalView('day'); };
  const goToWeekOf = (date) => { setWeekStart(startOfWeek(date, { weekStartsOn: 1 })); setCalView('week'); };

  // Conflict times for new job
  const [conflictTimes, setConflictTimes] = useState([]);
  useEffect(() => {
    if (!newJobStaff.length || !newJobDate) { setConflictTimes([]); return; }
    api.get('/jobs/conflicts', { params: { staff_ids: newJobStaff.join(','), date: newJobDate } })
      .then(({ data }) => setConflictTimes((data.conflicts || []).map(j => j.scheduled_time_start?.slice(0,5)).filter(Boolean)))
      .catch(() => setConflictTimes([]));
  }, [newJobStaff, newJobDate]);

  // Conflict times for detail view
  useEffect(() => {
    if (!showDetail?.assigned_staff?.length || !showDetail?.scheduled_date) { setDetailConflictTimes([]); return; }
    api.get('/jobs/conflicts', { params: { staff_ids: showDetail.assigned_staff.join(','), date: showDetail.scheduled_date } })
      .then(({ data }) => {
        const times = (data.conflicts || []).filter(j => j.id !== showDetail.id).map(j => j.scheduled_time_start?.slice(0,5)).filter(Boolean);
        setDetailConflictTimes(times);
      })
      .catch(() => setDetailConflictTimes([]));
    setDetailTime(showDetail.scheduled_time_start?.slice(0,5) || '');
    setManualEta(showDetail.eta_status?.eta_minutes ? `${showDetail.eta_status.eta_minutes} minutes` : showDetail.eta || '15 minutes');
    setNotifyEtaCustomer(true);
    setDetailNote('');
    setPendingDate(null);
  }, [showDetail]);

  const fetchJobSmsMessages = useCallback(async (jobId) => {
    if (!jobId) { setJobSmsMessages([]); return; }
    try {
      const { data } = await api.get('/messages', { params: { job_id: jobId, channel: 'sms', limit: 50 } });
      setJobSmsMessages(data || []);
    } catch { setJobSmsMessages([]); }
  }, []);

  useEffect(() => { fetchJobSmsMessages(showDetail?.id); }, [fetchJobSmsMessages, showDetail?.id]);

  const syncJobSmsMessage = async (messageId) => {
    setJobSmsSyncing(true);
    try {
      await api.post(`/messages/${messageId}/sync-twilio-status`);
      await fetchJobSmsMessages(showDetail?.id);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setJobSmsSyncing(false);
    }
  };

  const addJobNote = async () => {
    if (!detailNote.trim() || !showDetail) return;
    const { data } = await api.post(`/jobs/${showDetail.id}/notes`, { text: detailNote });
    setShowDetail(data);
    setDetailNote('');
    toast.success('Job note saved');
  };

  const sendDispatcherEta = async () => {
    if (!showDetail) return;
    try {
      const { data } = await api.post(`/jobs/${showDetail.id}/eta-update`, { eta: manualEta, notify_customer: notifyEtaCustomer, note: detailNote || '' });
      setShowDetail(data.job);
      toast.success(data.sms_result?.status === 'sent' ? 'ETA update sent' : 'ETA update saved');
      setDetailNote('');
      fetchJobs();
      fetchLateAlerts();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save ETA update');
    }
  };

  const sendLateArrivalAlert = async (jobId, notifyCustomer = false) => {
    try {
      const { data } = await api.post(`/jobs/${jobId}/late-arrival-alert`, { threshold_minutes: lateThreshold, notify_customer: notifyCustomer });
      if (showDetail?.id === jobId) setShowDetail(data.job);
      toast.success(notifyCustomer && data.sms_result?.status === 'sent' ? 'Late alert sent' : 'Late alert recorded');
      fetchJobs();
      fetchLateAlerts();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not record late alert');
    }
  };

  const proofUrl = (token) => `${window.location.origin}/proof/${token}`;

  const createProofPacket = async (job, open = false) => {
    try {
      const { data: packet } = await api.post(`/jobs/${job.id}/proof-packet`);
      const url = proofUrl(packet.packet.token);
      if (open) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Proof packet link copied');
      }
      const { data } = await api.get(`/jobs/${job.id}`);
      setShowDetail(data);
      fetchJobs();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not create proof packet');
    }
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const getJobsForDay = (day) => { const ds = format(day, 'yyyy-MM-dd'); return (calView === 'month' ? monthJobs : jobs).filter(j => j.scheduled_date === ds); };

  // Month view helpers
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = addDays(startOfWeek(addDays(monthEnd, 6), { weekStartsOn: 1 }), 6);
  const monthCalDays = eachDayOfInterval({ start: calStart, end: calEnd }).slice(0, 42);

  // Day view time blocks
  const dayTimeSlots = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00'];
  const dayJobs = calView === 'day' ? jobs : [];

  const jobsList = calView === 'month' ? monthJobs : jobs;

  return (
    <div className="-mx-3 space-y-4 bg-[#eef7ff] px-0 pb-8 pt-0 animate-fade-in sm:mx-0 sm:bg-transparent sm:px-0 sm:pt-0">
      <HighVisibilityPageHeader
        eyebrow="Dispatch board"
        title="Jobs"
        subtitle="Schedule, route, and keep every crew moving."
        countLabel={`${jobsList.length} jobs in view`}
        icon={BriefcaseBusiness}
        testId="jobs-high-visibility-header"
        titleTestId="jobs-title"
        actions={<Button className="h-14 w-full rounded-2xl bg-white px-5 font-black text-blue-700 hover:bg-blue-50 sm:w-auto" onClick={() => { resetNewJobForm(); setShowAdd(true); }} data-testid="add-job-button"><Plus className="w-5 h-5 mr-2" /> New Job</Button>}
      />

      <div className="space-y-4 px-3 sm:px-0">

      {/* Filters */}
      <JobsFilters searchText={searchText} setSearchText={setSearchText} staffFilter={staffFilter} setStaffFilter={setStaffFilter} fieldStaff={fieldStaff} statusFilter={statusFilter} setStatusFilter={setStatusFilter} allStatuses={allStatuses} />

      {/* View Switcher + Navigation */}
      <JobsViewControls calView={calView} setCalView={setCalView} weekStart={weekStart} setWeekStart={setWeekStart} monthDate={monthDate} setMonthDate={setMonthDate} dayDate={dayDate} setDayDate={setDayDate} />

      {/* Batch Bar */}
      <JobsBatchBar selectedCount={selected.size} allStatuses={allStatuses} batchStatus={batchStatus} fieldStaff={fieldStaff} batchAssign={batchAssign} clearSelection={clearSelection} />

      <JobsCalendarViews
        data={{ calView, monthCalDays, monthDate, weekDays, dragOverDay, dragJobId, dayTimeSlots, dayJobs, jobsList, selected }}
        actions={{ goToWeekOf, goToDay, handleDragOver, handleDragLeave, handleDrop, handleDragStart, handleDragEnd, setShowDetail, toggleSelectAll, toggleSelect }}
        helpers={{ getJobsForDay, getDBAColor, getStaffName, statusColors }}
      />

      <JobDialogs
        data={{ showAdd, error, createPrefill, selectedDBA, dbas, newJobDate, newJobTimeStart, newJobTimeEnd, newJobAddress, fieldStaff, newJobStaff, conflictTimes, showDetail, pendingDate, detailTime, detailConflictTimes, team, detailNote, manualEta, notifyEtaCustomer, lateAlerts, lateThreshold, jobSmsMessages, jobSmsSyncing }}
        actions={{ setShowAdd, resetNewJobForm, handleCreate, setNewJobDate, setNewJobTimeStart, setNewJobTimeEnd, setNewJobAddress, setNewJobStaff, setShowDetail, setPendingDate, updateStatus, rescheduleJob, setDetailTime, fetchJobs, setDetailNote, addJobNote, createProofPacket, navigate, api, setManualEta, setNotifyEtaCustomer, setLateThreshold, sendDispatcherEta, sendLateArrivalAlert, syncJobSmsMessage }}
        helpers={{ statusColors, allStatuses, canUseProofPacket, proofUrl, getDBAName, addHour }}
      />
      </div>
    </div>
  );
}
